import { NetworkStatus } from '@apollo/client';
import { LoadingIndicator } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setCurrentCarouselItem,
  setSelectedCalendarDate,
  setSelectedCalendarMonth,
} from 'actions';
import { CarouselBackButton } from 'components/CarouselBackButton';
import { Phrase } from 'components/Phrase';
import { ReactComponent as CalendarChevron } from 'components/svg/CalendarChevron.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useTimeSlotInfoForMonthQuery } from 'hooks/useTimeSlotInfoForMonthQuery';
import {
  CarouselReduxReducer,
  DateTimeFormat,
  ICarouselItemScheduleCalendarTopProps,
  IState,
} from 'typings';

import {
  Calendar,
  CalendarDates,
  CalendarHeading,
  CalendarNav,
  CalendarNavButton,
  CalendarRow,
  DayBlock,
  DayButton,
  DayHeading,
  Header,
} from './styles';

const weekdayNames = moment.weekdays();
const {
  DATE_COMPARISON: DATE_COMPARISON_FORMAT,
  MONTH_COMPARISON: MONTH_COMPARISON_FORMAT,
  MONTH_HEADER: MONTH_HEADER_FORMAT,
} = DateTimeFormat;

export const CarouselItemScheduleCalendarTop = ({
  headerPhraseId,
}: ICarouselItemScheduleCalendarTopProps) => {
  const {
    addressTimezone = '',
    selectedCalendarDate,
    selectedCalendarMonth,
  } = useSelector((state: IState) => ({
    addressTimezone: state.userInput.address.timezone,
    currentItemId: state.serviceCarousel.currentItemId,
    selectedCalendarDate: state.calendar.selectedCalendarDate,
    selectedCalendarMonth: state.calendar.selectedCalendarMonth,
  }));
  const dispatch = useThunkDispatch();
  const theme = useTheme();
  const [firstAvailableDate, setFirstAvailableDate] = useState('');
  const firstAvailableDateButtonRef = useRef<HTMLButtonElement | null>(null);

  const {
    data: {
      timeSlotInfoForMonth: { isPmCampaign = false, timeSlots = [] } = {},
    } = {},
    loading: isTimeSlotInfoForMonthLoading,
    networkStatus,
  } = useTimeSlotInfoForMonthQuery();

  const [firstTimeSlot] = timeSlots;
  const firstTimeSlotStartTime = firstTimeSlot && firstTimeSlot.startTime;

  const firstDateOfMonth = selectedCalendarMonth
    ? moment(selectedCalendarMonth, MONTH_COMPARISON_FORMAT)
    : moment();
  const selectedMonthString = firstDateOfMonth.format(MONTH_COMPARISON_FORMAT);
  const todayDateMoment = moment();
  const todayDateString = todayDateMoment.format(DATE_COMPARISON_FORMAT);
  const todayMonthString = todayDateMoment.format(MONTH_COMPARISON_FORMAT);

  const monthName = firstDateOfMonth.format(MONTH_HEADER_FORMAT);
  const daysInMonth = firstDateOfMonth.daysInMonth();
  const daysInFirstWeek = 7 - firstDateOfMonth.day();
  const weekRowsInMonth = Math.ceil((daysInMonth - daysInFirstWeek) / 7) + 1;

  const weekRowIndexes = Array(weekRowsInMonth)
    .fill(null)
    .map((_, index) => index);
  const dayNumbersForMonth = Array(daysInMonth)
    .fill(null)
    .map((_, index) => index + 1);

  const dayNumbersWithSlots = timeSlots.reduce(
    (results: Array<number>, { startTime }) => {
      const startTimeMoment = moment.tz(startTime, addressTimezone);
      const startTimeMonth = startTimeMoment.format(MONTH_COMPARISON_FORMAT);

      if (startTimeMonth === selectedCalendarMonth) {
        const startTimeDay = startTimeMoment.date();

        if (!results.includes(startTimeDay)) {
          return [...results, startTimeDay];
        }
      }

      return results;
    },
    [],
  );

  const isVisiblyLoading =
    isTimeSlotInfoForMonthLoading && networkStatus !== NetworkStatus.poll;

  const handleDayClick = (dateString: string) => {
    dispatch(setSelectedCalendarDate(dateString));
  };

  const handlePreviousMonthClick = () => {
    const currentMonthMoment = selectedCalendarMonth
      ? moment(selectedCalendarMonth, MONTH_COMPARISON_FORMAT)
      : moment();
    const prevMonth = currentMonthMoment
      .subtract(1, 'months')
      .format(MONTH_COMPARISON_FORMAT);

    dispatch(setSelectedCalendarMonth(prevMonth));
  };

  const handleNextMonthClick = () => {
    const currentMonthMoment = selectedCalendarMonth
      ? moment(selectedCalendarMonth, MONTH_COMPARISON_FORMAT)
      : moment();
    const nextMonth = currentMonthMoment
      .add(1, 'months')
      .format(MONTH_COMPARISON_FORMAT);

    dispatch(setSelectedCalendarMonth(nextMonth));
  };

  // Handle when `selectedCalendarMonth` is `null`, which happens when we are
  // querying for the next available set of timeslots
  useEffect(() => {
    if (!selectedCalendarMonth && firstTimeSlotStartTime) {
      const firstTimeSlotMoment = moment(firstTimeSlotStartTime);
      const nextSelectedCalendarMonth = firstTimeSlotMoment.format(
        MONTH_COMPARISON_FORMAT,
      );
      const nextFirstAvailableDate = firstTimeSlotMoment.format(
        DATE_COMPARISON_FORMAT,
      );

      dispatch(setSelectedCalendarMonth(nextSelectedCalendarMonth));
      dispatch(setSelectedCalendarDate(nextFirstAvailableDate));
      setFirstAvailableDate(nextFirstAvailableDate);
    }
  }, [dispatch, firstTimeSlotStartTime, selectedCalendarMonth]);

  // Redirect to show "no time slots" screen if it is a PM campaign and there
  // are no timeslots
  useEffect(() => {
    if (isPmCampaign && timeSlots.length === 0) {
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          CarouselItemId.ScheduleMaintenanceNoTimeSlots,
        ),
      );
    }
  }, [dispatch, isPmCampaign, timeSlots.length]);

  return (
    <>
      <CarouselBackButton />
      <Header>
        <Phrase id={headerPhraseId} />
      </Header>
      <Calendar>
        <CalendarNav>
          <CalendarNavButton
            aria-label="Previous month"
            direction="left"
            disabled={selectedMonthString === todayMonthString}
            onClick={handlePreviousMonthClick}
          >
            <CalendarChevron />
          </CalendarNavButton>
          <CalendarHeading>{monthName}</CalendarHeading>
          <CalendarNavButton
            aria-label="Next month"
            direction="right"
            onClick={handleNextMonthClick}
          >
            <CalendarChevron />
          </CalendarNavButton>
        </CalendarNav>
        <CalendarRow>
          {weekdayNames.map((weekday) => (
            <DayHeading key={`Calendar-day-${weekday}`}>
              {weekday[0]}
            </DayHeading>
          ))}
        </CalendarRow>
        <CalendarDates>
          <LoadingIndicator
            isLoading={isVisiblyLoading}
            label={Phrase({ id: 'SCHEDULE_CALENDAR.LOADING' })}
            themeValues={{
              labelColor: theme.topForegroundPrimary,
            }}
          />
          {weekRowIndexes.map((rowIndex) => {
            const isFirstRow = rowIndex === 0;
            const rowKey = `CalendarRow-${rowIndex}`;
            const dayNumbersForRow = dayNumbersForMonth.splice(
              0,
              isFirstRow ? daysInFirstWeek : 7,
            );
            const dateMoment = selectedCalendarMonth
              ? moment(selectedCalendarMonth, MONTH_COMPARISON_FORMAT)
              : moment();

            return (
              <CalendarRow align={isFirstRow ? 'right' : 'left'} key={rowKey}>
                {dayNumbersForRow.map((day) => {
                  const dateString = dateMoment
                    .date(day)
                    .format(DATE_COMPARISON_FORMAT);
                  const isDisabled =
                    isVisiblyLoading || !dayNumbersWithSlots.includes(day);
                  const isSelected =
                    !isDisabled && dateString === selectedCalendarDate;
                  const isToday = dateString === todayDateString;
                  const isFirstAvailableDate =
                    dateString === firstAvailableDate;

                  return (
                    <DayBlock key={dateString}>
                      <DayButton
                        disabled={isDisabled}
                        isHidden={isVisiblyLoading}
                        isSelected={isSelected}
                        isToday={isToday}
                        onClick={() => handleDayClick(dateString)}
                        ref={
                          isFirstAvailableDate
                            ? firstAvailableDateButtonRef
                            : undefined
                        }
                      >
                        {day}
                      </DayButton>
                    </DayBlock>
                  );
                })}
              </CalendarRow>
            );
          })}
        </CalendarDates>
      </Calendar>
    </>
  );
};
